/**
 * app-shell - Main application shell component
 * Contains header, router outlet, and toast notifications
 * Handles navigation events and coordinates with auth state
 */
import { BaseElement } from '../base-element';
import { authActions, authState } from '../../state/auth.state';
import './app-header';
import './app-toast';
import '../pages/trials-page';
import '../pages/login-page';
import '../pages/auth-callback-page';
import '../pages/registration/registration-page';
import '../pages/secretary/secretary-list-page';
import '../pages/secretary/secretary-detail-page';
import { getAuthCallbackPath } from '../../auth-navigation';

export class AppShell extends BaseElement {
  private currentPath: string = '/';
  private readonly popStateHandler = () => this.syncWithLocation();
  private readonly pageShowHandler = () => this.syncWithLocation();

  connectedCallback(): void {
    this.currentPath = window.location.pathname || '/';

    this.watch(authState, () => {
      this.updateRender();
    });

    this.updateRender();
    this.setupEventListeners();

    window.addEventListener('popstate', this.popStateHandler);
    window.addEventListener('pageshow', this.pageShowHandler);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener('popstate', this.popStateHandler);
    window.removeEventListener('pageshow', this.pageShowHandler);
  }

  private setupEventListeners(): void {
    this.addEventListener('navigate', ((e: CustomEvent<{ path: string }>) => {
      this.navigateTo(e.detail.path);
    }) as EventListener);

    this.addEventListener('login-click', () => {
      const scheduleRedirect =
        typeof window.requestAnimationFrame === 'function'
          ? window.requestAnimationFrame.bind(window)
          : (callback: FrameRequestCallback) => window.setTimeout(() => callback(0), 0);

      scheduleRedirect(() => {
        void authActions.login();
      });
    });

    this.addEventListener('logout-click', () => {
      void authActions.logout();
      this.navigateTo('/');
    });
  }

  private syncWithLocation(): void {
    this.currentPath = window.location.pathname || '/';
    this.updateRender();
    this.emit('route-changed', { path: this.currentPath });
  }

  public navigateTo(path: string): void {
    if (this.currentPath !== path) {
      this.currentPath = path;
      window.history.pushState({}, '', path);
      this.updateRender();
      this.emit('route-changed', { path: this.currentPath });
    }
  }

  public getCurrentPath(): string {
    return this.currentPath;
  }

  private renderLoginContent(): string {
    if (authState.get().isLoading) {
      return `
        <div class="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-6 text-center" data-testid="login-interstitial">
          <dt-spinner size="large"></dt-spinner>
          <div class="space-y-2">
            <h1 class="text-2xl font-semibold text-neutral-900 dark:text-white">Just a second while we get you logged in...</h1>
            <p class="max-w-md text-sm text-neutral-500 dark:text-neutral-400">You can use your browser Back button again if you want to stay in the app instead.</p>
          </div>
        </div>
      `;
    }

    return `<dt-login-page data-testid="login-page"></dt-login-page>`;
  }

  private renderAuthHandoffOverlay(): string {
    if (!authState.get().isLoading || this.currentPath === '/login') {
      return '';
    }

    return `
      <div class="fixed inset-0 z-50 flex items-center justify-center bg-neutral-950/55 px-4 backdrop-blur-sm" data-testid="auth-handoff-overlay">
        <div class="w-full max-w-md rounded-3xl bg-white p-8 text-center shadow-2xl dark:bg-surface-dark">
          <div class="flex flex-col items-center gap-4">
            <dt-spinner size="large"></dt-spinner>
            <div class="space-y-2">
              <h2 class="text-2xl font-semibold text-neutral-900 dark:text-white">Just a second while we get you logged in...</h2>
              <p class="text-sm text-neutral-500 dark:text-neutral-400">We’re opening the secure sign-in flow and will bring you right back here when it finishes.</p>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private renderContent(): string {
    const path = this.currentPath;

    if (path === '/' || path === '/trials') {
      return `<dt-trials-page data-testid="trials-page"></dt-trials-page>`;
    }

    const registerMatch = path.match(/^\/trials\/([^/]+)\/register$/);
    if (registerMatch) {
      const trialEventId = registerMatch[1];
      return `<dt-registration-page trial-event-id="${trialEventId}" data-testid="registration-page"></dt-registration-page>`;
    }

    const secretaryDetailMatch = path.match(/^\/secretary\/entries\/([^/]+)\/([^/]+)$/);
    if (secretaryDetailMatch) {
      const trialEventId = secretaryDetailMatch[1];
      const entryId = secretaryDetailMatch[2];
      return `<dt-secretary-detail-page trial-event-id="${trialEventId}" entry-id="${entryId}" data-testid="secretary-detail-page-wrapper"></dt-secretary-detail-page>`;
    }

    if (path === '/secretary') {
      return `<dt-secretary-page data-testid="secretary-page-wrapper"></dt-secretary-page>`;
    }

    if (path === '/login') {
      return this.renderLoginContent();
    }

    if (path === getAuthCallbackPath()) {
      return `<dt-auth-callback-page data-testid="auth-callback-page"></dt-auth-callback-page>`;
    }

    const routes: Record<string, { title: string; icon: string }> = {
      '/register': { title: 'Register', icon: '📝' },
      '/my-entries': { title: 'My Entries', icon: '📋' },
      '/profile': { title: 'Profile', icon: '👤' },
    };

    let matchedRoute = routes[path];

    if (!matchedRoute && path.startsWith('/register/')) {
      matchedRoute = routes['/register'];
    }

    if (!matchedRoute) {
      return `
        <div class="flex min-h-[60vh] flex-col items-center justify-center p-6 text-center" data-route="404">
          <div class="mb-4 text-5xl">🔍</div>
          <h1 class="mb-2 text-2xl font-bold text-neutral-900 dark:text-white">Page Not Found</h1>
          <p class="text-sm text-neutral-500 dark:text-neutral-400">The page you're looking for doesn't exist.</p>
        </div>
      `;
    }

    return `
      <div class="flex min-h-[60vh] flex-col items-center justify-center p-6 text-center" data-route="${path}">
        <div class="mb-4 text-5xl">${matchedRoute.icon}</div>
        <h1 class="mb-2 text-2xl font-bold text-neutral-900 dark:text-white">${matchedRoute.title}</h1>
        <p class="text-sm text-neutral-500 dark:text-neutral-400">This page will be implemented in a future work item.</p>
      </div>
    `;
  }

  private updateRender(): void {
    this.className = 'bg-background-light dark:bg-background-dark';

    this.render(`
      <div class="flex min-h-screen flex-col">
        <app-header></app-header>

        <main class="flex-1 pt-0">
          <div class="mx-auto w-full max-w-7xl p-4 md:p-6 lg:p-8">
            <div class="min-h-[calc(100vh-200px)]" data-testid="router-outlet">
              ${this.renderContent()}
            </div>
          </div>
        </main>
      </div>

      ${this.renderAuthHandoffOverlay()}

      <app-toast></app-toast>
    `);
  }
}

customElements.define('app-shell', AppShell);
